import {AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {BehaviorSubject, fromEvent, Observable, Subject} from 'rxjs';
import {debounceTime, map, startWith, take, takeUntil} from 'rxjs/operators';

import {AcceptRejectService, AssetsService, AuthenticationService, DialogService} from '../../services';
import {FinalDisplayComponent} from './final-display/final-display.component';
import {ConfirmRejectionComponent} from '../confirm-rejection/confirm-rejection.component';
import {Asset, Response, Service} from '../../models';
import _ from 'lodash';
import {MatDialogRef} from '@angular/material/dialog';

const THUMBNAIL_ONLY_TYPES = [
    'ai', 'bmp', 'doc', 'eps', 'gif', 'indd', 'mp3', 'no-support',
    'pdf', 'ppt', 'psd', 'raw', 'tif', 'txt', 'wav', 'xls', 'zip'
];

@Component({
    selector: 'prism-product-carousel',
    templateUrl: './product-carousel.component.html',
    styleUrls: ['./product-carousel.component.scss']
})
export class ProductCarouselComponent implements OnInit, AfterViewInit, OnDestroy {
    memberData$ = this.authService.memberData$;
    pathToFinals;
    finalAsset: {
        finalLoaded: boolean;
        element: HTMLImageElement;
        type: 'image' | 'video' | 'video-preview-image';
        videoUrl: string
    };
    initialIndex: number;
    selectedImageTitle: string;
    serviceTypeSnakeCase: string;
    productTitle: string;
    currentIndex: number;
    service: Service;
    assets: Asset[];
    filteredAssets;
    filterConfig: { visible: boolean, hidden: boolean, rejected: boolean };
    displayFilterMenu: boolean;
    destroyer$: Subject<void>;
    getFinalSubDestroyer$: Subject<void>;
    rejectionReasonFocussed: boolean;
    rejectionsPending$: Observable<any>;
    contentType: string;
    filename: string;
    dimensions: { width: number, height: number };
    currentlyTyping: boolean;
    serviceKeyValueRef: {
        key: string,
        value: Asset[]
    };
    rejectedAssets: {
        [serviceId: string]: {
            [filename: string]: string
        }
    };
    rejectionTextRendered: BehaviorSubject<any>;
    resendValid;
    finalAssets: FinalAssets;
    selectedAssetFinalPath: string;
    finalDataLoaded: boolean;
    private selectionInitialized: boolean;

    @HostListener('document:click', ['$event']) clickedOutside() {
        this.displayFilterMenu = false;
    }

    @ViewChildren('rejectText') rejectTexts: QueryList<ElementRef<HTMLTextAreaElement>>;
    @ViewChildren('scrollAssets') scrollAssets: QueryList<ElementRef<HTMLDivElement>>;
    @ViewChild('finalDisplay', {static: false}) finalDisplayComponent: FinalDisplayComponent;

    constructor(
        private dialogService: DialogService,
        private assetsService: AssetsService,
        private authService: AuthenticationService,
        private acceptRejectService: AcceptRejectService,
        private dialogRef: MatDialogRef<ProductCarouselComponent>
    ) {
        this.displayFilterMenu = false;
        this.destroyer$ = new Subject<void>();
        this.getFinalSubDestroyer$ = new Subject<void>();
        this.rejectionReasonFocussed = false;
        this.rejectionsPending$ = this.acceptRejectService.rejectionPending$;
        this.memberData$ = this.authService.memberData$;
        this.serviceKeyValueRef = null;
        this.rejectionTextRendered = new BehaviorSubject<any>(null);
        this.rejectedAssets = {};
        this.resendValid = {};
        this.finalAssets = {};
        this.finalDataLoaded = false;
        this.selectionInitialized = false;
        this.finalAsset = {
            finalLoaded: false,
            element: null,
            type: null,
            videoUrl: null
        };
        this.filterConfig = {
            visible: true,
            hidden: true,
            rejected: true
        };
    }

    ngOnInit() {
        let pathsToFinal = [];
        const {assets, service, serviceKeyValueRef, filter, serviceTypeSnakeCase}: {
            assets: Asset[],
            service: Service,
            serviceKeyValueRef: {
                key: string,
                value: Asset[]
            },
            filter: {
                visible: boolean,
                hidden: boolean,
                rejected: boolean
            },
            serviceTypeSnakeCase: string
        } = this.dialogService.inputData;

        this.filterConfig = _.cloneDeep(filter);
        this.serviceTypeSnakeCase = serviceTypeSnakeCase;
        this.assets = _.cloneDeep(assets);
        this.serviceKeyValueRef = serviceKeyValueRef;
        this.filteredAssets = this.filterAssets();
        let assetsToFetch: Asset[] = [];
        this.assets.forEach((asset) => {
            let image = new Image();
            image.src = asset.url;
            this.finalAssets[asset.finalPath] = {
                element: image,
                type: (asset.contentType.startsWith('video/')) ? 'video-preview-image' : 'image',
                finalLoaded: false,
                videoUrl: null
            };

            if (this.isThumbnailOnlyAsset(asset)) {
                image.onload = () => {
                    this.finalAssets[asset.finalPath] = {
                        ...this.finalAssets[asset.finalPath],
                        finalLoaded: true
                    };
                    if (this.selectedAssetFinalPath === asset.finalPath) {
                        this.finalAsset = this.finalAssets[asset.finalPath];
                    }
                };
            } else {
                pathsToFinal.push(asset.finalPath);
                assetsToFetch.push(asset);
            }
        });
        this.service = service;

        if (pathsToFinal.length) {
            this.assetsService.getProcessedAssetFinalSignedUrls(pathsToFinal)
                .pipe(take(1))
                .subscribe((res: Response) => {
                    let {pathsToFinal: returnedPaths} = res.data;
                    returnedPaths.forEach((data, index) => {
                        const asset = assetsToFetch[index];
                        const {url, contentType, width, height, isLuxEngine} = data;
                        asset.isLuxEngine = isLuxEngine;

                        if (contentType.startsWith('video')) {
                            this.finalAssets[asset.finalPath] = {
                                element: null,
                                type: 'video',
                                finalLoaded: true,
                                videoUrl: url
                            };
                            if (this.selectedAssetFinalPath === asset.finalPath) {
                                this.finalAsset = this.finalAssets[asset.finalPath];
                            }

                        } else {
                            asset.finalUrl = url;
                            asset.contentType = contentType;
                            asset.dimensions = {width, height};
                            let image = new Image();
                            image.onload = () => {
                                this.finalAssets[asset.finalPath] = {
                                    element: image,
                                    type: 'image',
                                    finalLoaded: true,
                                    videoUrl: null
                                };

                                if (this.selectedAssetFinalPath === asset.finalPath) {
                                    this.finalAsset = this.finalAssets[asset.finalPath];
                                }
                            };
                            image.src = url;
                        }
                    });
                    this.finalDataLoaded = true;
                    this.trySelectInitialAsset();
                });
        } else {
            this.finalDataLoaded = true;
        }

        this.rejectedAssets[this.serviceKeyValueRef['key']] = {};
        this.resendValid[this.serviceKeyValueRef['key']] = false;
        this.rejectionTextRendered.pipe(debounceTime(0)).subscribe(() => {
            let rejectTextsList = this.rejectTexts.toArray();
            this.filteredAssets.forEach((item, index) => {
                if (this.rejectedAssets[this.service.id][item.filename]) {
                    if (rejectTextsList[index]) {
                        rejectTextsList[index].nativeElement.innerHTML = this.rejectedAssets[this.service.id][item.filename];
                    }
                }
            });
        });

        // Subscribe to bulk rejection updates to refresh the filter
        this.acceptRejectService.bulkRejectionsUpdated$
            .pipe(takeUntil(this.destroyer$))
            .subscribe((data) => {
                const { serviceId } = data;
                if (this.serviceKeyValueRef && this.serviceKeyValueRef.key === serviceId) {
                    this.filteredAssets = this.filterAssets();
                    this.rejectionTextRendered.next(null);
                    if (this.filteredAssets.length > 0 && this.currentIndex >= this.filteredAssets.length) {
                        this.currentIndex = 0;
                        this.selectImageToDisplay(this.currentIndex, this.filteredAssets[this.currentIndex]);
                    } else if (this.filteredAssets.length === 0) {
                        this.getFinalSubDestroyer$.next();
                    }
                }
            });
    }

    ngAfterViewInit() {
        this.trySelectInitialAsset();
    }

    private trySelectInitialAsset() {
        if (this.selectionInitialized || !this.finalDataLoaded) {
            return;
        }
        this.selectionInitialized = true;
        setTimeout(_ => {
            const initialAsset = this.assets[this.initialIndex];
            let index = this.filteredAssets.findIndex((asset: Asset) => asset.finalPath === initialAsset?.finalPath);
            if (index === -1) {
                index = 0;
            }
            this.selectImageToDisplay(index, this.filteredAssets[index]);
        }, 10);
    }

    filterSelected(filter: 'visible' | 'hidden' | 'rejected') {
        this.filterConfig[filter] = !this.filterConfig[filter];
        this.filteredAssets = this.filterAssets();
        this.rejectionTextRendered.next(null);
        this.currentIndex = 0;
        if (this.filteredAssets.length > 0) {
            this.selectImageToDisplay(this.currentIndex, this.filteredAssets[this.currentIndex]);
        } else {
            this.getFinalSubDestroyer$.next();
        }
    }

    filterAssets() {
        return this.assets.filter((asset) => {
            let allowVisibleAsset = this.filterConfig.visible && !asset.hidden;
            let allowHiddenAsset = this.filterConfig.hidden && asset.hidden;
            let allowRejectedAsset = this.filterConfig.rejected && !!(asset.finalRejectReason || asset.savedRejectReason);
            return (allowVisibleAsset || allowHiddenAsset || allowRejectedAsset);
        });
    }

    private isThumbnailOnlyAsset(asset: Asset): boolean {
        const contentType = (asset.contentType || '').toLowerCase();
        const extension = asset.filename?.split('.').pop()?.toLowerCase() || '';
        return THUMBNAIL_ONLY_TYPES.includes(contentType) || THUMBNAIL_ONLY_TYPES.includes(extension);
    }

    preventClickThrough(event) {
        event.stopPropagation();
        this.displayFilterMenu = !this.displayFilterMenu;
    }

    selectImageToDisplay(i, thumb?) {
        if (thumb.filename !== this.selectedImageTitle) {
            this.selectedAssetFinalPath = thumb.finalPath;
            this.getFinalSubDestroyer$.next();
            this.contentType = thumb.contentType;
            this.filename = thumb.filename.replace(/^\d+_/, '');
            this.finalAsset = this.finalAssets[thumb.finalPath];
            this.selectedImageTitle = this.filteredAssets[i];
            this.currentIndex = i;

            let elementArray = this.scrollAssets.toArray();
            if (elementArray.length > 0) {
                const element: HTMLDivElement = this.scrollAssets.toArray()[i].nativeElement;
                const rejectElement: HTMLTextAreaElement = this.rejectTexts.toArray()[i].nativeElement;
                rejectElement.focus({preventScroll: false});
                element.scrollIntoView({
                    behavior: 'auto',
                    block: 'center'
                });
            }
        }
    }

    closeDialog() {
        this.dialogRef.close();
    }

    insertUrl(url) {
        return `url("${url}")`;
    }

    updateRejectReason(i, startValue) {
        this.rejectionReasonFocussed = true;
        this.currentlyTyping = true;
        const inputTarget = this.rejectTexts.toArray()[i].nativeElement;
        const input$ = fromEvent(inputTarget, 'input')
            .pipe(
                map((event: any) => {
                    return event.target.value;
                }),
                startWith(startValue),
                debounceTime(300),
                takeUntil(this.destroyer$)
            );

        input$
            .subscribe(
                reason => {
                    let filename = this.filteredAssets[i].filename;
                    if (!!reason) {
                        this.rejectedAssets[this.service.id][filename] = reason;
                    } else {
                        this.rejectedAssets[this.service.id][filename] = null;
                    }
                    this.resendValid[this.service.id] = Object.values(this.rejectedAssets[this.service.id]).length > 0;
                    this.acceptRejectService.modifyAssetRejection(this.service.id, reason, filename);
                    this.destroyer$.next();
                },
                err => console.error('Update Rejections error:', err)
            );
    }

    openRejectionConfirmation(serviceId: string) {
        this.dialogService.openDialog(ConfirmRejectionComponent, {
            service: this.service,
            pathToFinals: this.pathToFinals,
            serviceKeyValueRef: this.serviceKeyValueRef,
            rejectedAssets: this.rejectedAssets,
            serviceTypeSnakeCase: this.serviceTypeSnakeCase
        });
    }

    finalNavButtonClicked(target: 'previous' | 'next') {
        if (target === 'previous' && this.currentIndex - 1 > -1) {
            this.currentIndex -= 1;
            this.selectImageToDisplay(this.currentIndex, this.filteredAssets[this.currentIndex]);
        } else if (target === 'next' && this.currentIndex + 1 < this.filteredAssets.length) {
            this.currentIndex += 1;
            this.selectImageToDisplay(this.currentIndex, this.filteredAssets[this.currentIndex]);
        }
    }

    clickedInRejectionText() {
        this.rejectionReasonFocussed = true;
        this.currentlyTyping = true;
    }

    handleRejectionTextBlur() {
        this.rejectionReasonFocussed = false;
        this.currentlyTyping = false;
    }

    ngOnDestroy() {
        this.destroyer$.next();
    }


    @HostListener('window:keyup', ['$event'])
    checkCurrentlyTyping(event) {
        const keyCode = event.keyCode;

        if (keyCode === 8 && !event.target.value) {
            this.currentlyTyping = false;
            this.rejectionReasonFocussed = false;
        }
    }

    // NAVIGATION
    @HostListener('window:keydown', ['$event'])
    setNextSlide(event) {
        const keyCode = event.keyCode;

        if (!this.currentlyTyping || !this.rejectionReasonFocussed && !event.target.value) {
            let targetIndex;
            if (keyCode === 37 || keyCode === 38) {
                targetIndex = this.currentIndex - 1;
            }
            if (keyCode === 39 || keyCode === 40) {
                targetIndex = this.currentIndex + 1;
            }
            if (targetIndex > -1 && targetIndex < this.scrollAssets.length) {
                this.selectImageToDisplay(targetIndex, this.filteredAssets[targetIndex]);
            }
        }
    }
}

interface FinalAssets {
    [finalPath: string]: {
        finalLoaded: boolean;
        element: HTMLImageElement;
        type: 'image' | 'video' | 'video-preview-image'
        videoUrl: string
    };
}
